import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { catchError, firstValueFrom, forkJoin, map, of } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';

import {
  DropdownOption,
  PKL_GRP,
} from '../../../core/shared/models/picklist.model';

import { PickListService } from '../../../core/shared/services/picklist.service';
import { CraditService } from '../shared/services/cradit.service';
import { BackButton } from '../../shared/buttons/back-button';
import { RichTextPipe } from '../shared/utils/rich-text.pipe';
import { TableCellPipe } from '../shared/utils/table-cell.pipe';
import { isRichTextHtml, joinRichText } from '../shared/utils/rich-text.util';
import { scopedListLabel } from '../shared/utils/scoped-numbering.util';
import {
  CreditSectionBlock,
  ReportSection,
  ReportTemplateMetadata,
  SectionJsonData,
  NestedReportBlock,
  NestedTable,
  NestedTableColumn,
  FinancialAnalysisReportData,
  FinancialAnalysisReportRow,
  RepaymentPlanReportData,
  RepaymentPlanReportRow,
  DisbursementRepaymentSummaryReportData,
  DisbursementRepaymentSummaryReportRow,
} from '../shared/models/Credit report.model';
import {
  CreditReportExportService,
  CapturedTableImage,
  CapturedTableImages,
  CapturedTableKey,
  BoardDirectorReportRow,
  KeyManagementReportRow,
  CommitteeReportRow,
  OrganogramReportImage,
  AttachmentReportItem,
  RECOMMENDATION_SIGNATURE_BLOCKS,
  SUMMARY_TERMS_REF_VAL_SEQ,
} from '../shared/services/creditreportexport.service ';
import { Setup } from '../../setup/shared/service/setup';
import {
  CREDIT_PROPOSAL_ORGANOGRAM_DOC_SEQ,
  CREDIT_PROPOSAL_REPORT_ATTACHMENT_DOC_SEQS,
} from '../shared/models/credit-proposal-documents.constants';
import { LeadService } from '../../leads/shared/service/leads.service';
import { MODULE_SEQ } from '../../../core/shared/models/picklist.model';
import { cleanBase64, getFileInfo } from '../../leads/shared/utils/document-file.util';
import { OnboradingService } from '../../onboarding/shared/service/onboarding.service';
import { CREDIT_PROPOSAL_TEMPLATE } from '../../../../features/document-builder/data/credit-proposal-template';
import { SectionType } from '../shared/models/section-type.enum';
import {
  extractBaseExposure,
  extractExistingRepayment,
  extractExistingDisbursement,
} from '../shared/utils/repayment-api.util';
import {
  RepaymentPlanTypeGroup,
  splitRepaymentPlanByType,
} from '../shared/utils/repayment-plan-by-type.util';
import {
  buildRepaymentTrancheRowsFromSaved,
  flattenRepaymentTrancheRows,
} from '../shared/utils/repayment-plan-rows.util';

interface SectionApiOutcome {
  response: unknown;
  error: unknown;
}



/** One repayment term type's Tentative Disbursements and Repayment Plan table. */
interface DisbursementSummaryTypeGroup {
  /** Repayment term name (e.g. Conventional); '' when unknown. */
  type: string;
  data: DisbursementRepaymentSummaryReportData;
}

@Component({
  selector: 'app-genrate-credit-report',
  standalone: true,
  imports: [
    CommonModule,
    BackButton,
    TranslatePipe,
    RichTextPipe,
    TableCellPipe,
  ],
  templateUrl: './genrate-credit-report.html',
  styleUrl: './genrate-credit-report.css',
})
export class GenrateCreditReport implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly exportService = inject(CreditReportExportService);

  creditPerposal = 0;

  loading = true;
  errorMessage = '';
  exporting = false;
  institutionName = '';

  allSectionData: CreditSectionBlock[] = [];
  reportSections: ReportSection[] = [];

  /** Heading label per section (by refValSeq) and of Financial Analysis, as in the Word report. */
  sectionHeadingLabels: { bySection: Map<number, string>; financialAnalysis: string } = {
    bySection: new Map(),
    financialAnalysis: '',
  };

  /** Opening exposure, shared by the Repayment Plan and Disbursement & Repayment Summary report sections. */
  openingExposure = 0;
  existingRepayments = 0;
  existingDisbursements = 0;
  financialAnalysisSummaryRows: FinancialAnalysisReportRow[] = [];
  financialAnalysisSummaryColumns: string[] = [];

  /**
   * Tranche rows of the currently saved repayment plan (live APIs). Preferred
   * over the Repayment Plan section block, which is only a first-save
   * snapshot and goes stale after a re-calculate + save.
   */
  private savedRepaymentTrancheRows: Record<string, unknown>[] = [];

  /** Repayment term type codes (`typ`, ref code group 13) the user added in Repayment Terms, in saved order. */
  private repaymentTermCodes: unknown[] = [];

  /** Profile of Board of Directors (Annexure-III (a)) — native table, shown above Financial Analysis. */
  boardOfDirectors: BoardDirectorReportRow[] = [];

  /** List of Key Management (Annexure-III (b)) — native table, shown after Board of Directors. */
  keyManagement: KeyManagementReportRow[] = [];

  /** Committee Details (Annexure-III (c)) — native table, shown after Key Management. */
  committees: CommitteeReportRow[] = [];

  /** Organogram (Annexure-III (d)) uploaded on the Credit Proposal's Documents tab. */
  organogram: OrganogramReportImage | null = null;

  /** Attachments tab uploads, shown after the Organogram. */
  attachments: AttachmentReportItem[] = [];

  /** refValSeq constants mapped explicitly (not via blkTyp) per report-only section. */
  private readonly FINANCIAL_ANALYSIS_DETAILS_REF_VAL_SEQ = 2647;
  private readonly OLD_INSTITUTIONAL_HIGHLIGHTS_REF_VAL_SEQ = 2520;
  private readonly COMPANY_INTRODUCTION_REF_VAL_SEQ = 2519;
  private readonly DISBURSEMENT_REPAYMENT_SUMMARY_REF_VAL_SEQ = 2645;
  private readonly REPAYMENT_PLAN_REF_VAL_SEQ = 2646;

  /** "2.1"-style heading label: a sub-section, indented like the Word report's. */
  isSubSectionLabel(label: string | undefined): boolean {
    return /^\d+\.\d+$/.test(label ?? '');
  }

  /** Introduction starts a new page under the client name / "Credit Proposal" title. */
  readonly introductionRefValSeq = 2525;

  /** Summary Terms and Conditions carries the MRC / BRC signature page beneath it. */
  readonly summaryTermsRefValSeq = SUMMARY_TERMS_REF_VAL_SEQ;
  readonly recommendationSignatureBlocks = RECOMMENDATION_SIGNATURE_BLOCKS;

  private readonly templateMetadata: Map<number, ReportTemplateMetadata> =
    this.buildTemplateMetadata();

  constructor(
    private creditService: CraditService,
    private route: ActivatedRoute,
    private pickListService: PickListService,
    private onboardingService: OnboradingService,
    private leadService: LeadService,
    private setup: Setup,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.creditPerposal = this.getCreditProposalSequence();

    this.institutionName = String(
      history.state?.institutionName ??
        this.route.snapshot.queryParamMap.get('institutionName') ??
        '',
    ).trim();

    if (this.creditPerposal <= 0) {
      this.loading = false;
      this.errorMessage =
        'CREDIT_REPORT.PROPOSAL_SEQ_NA';

      return;
    }

    this.loadReportData();
  }

  /* ====================================================== */
  /* TEMPLATE METADATA — order & title source of truth      */
  /* ====================================================== */

  private buildTemplateMetadata(): Map<
    number,
    ReportTemplateMetadata
  > {
    const metadata = new Map<
      number,
      ReportTemplateMetadata
    >();

    CREDIT_PROPOSAL_TEMPLATE.sections
      .filter((section) => section.enabled)
      .forEach((section) => {
        const refValSeq = Number(
          section.config?.refValSeq ?? 0,
        );

        if (
          !Number.isFinite(refValSeq) ||
          refValSeq <= 0
        ) {
          // refValSeq <= 0 marks API-driven builder sections
          // (Terms Parameters, Repayment Plan, etc.) that are
          // not sourced from getAllSection().
          return;
        }

        metadata.set(refValSeq, {
          sectionId: section.id,
          type: section.type,
          refValSeq,
          templateOrder: Number(section.order),
          templateTitle: String(
            section.title ?? '',
          ).trim(),
        });
      });

    return metadata;
  }

  /* ====================================================== */
  /* LOAD BOTH SOURCES DETERMINISTICALLY                    */
  /* ====================================================== */

  private loadReportData(): void {
    forkJoin({
      titleStore: this.pickListService
        .loadCodesAsync([
          PKL_GRP.SUMMARY_TITLE,
          PKL_GRP.FACILITY_TYPE,
        ])
        .pipe(catchError(() => of(null))),

      sectionOutcome: this.creditService
        .getAllSection(this.creditPerposal)
        .pipe(
          map(
            (response: unknown): SectionApiOutcome => ({
              response,
              error: null,
            }),
          ),
          catchError((error: unknown) =>
            of<SectionApiOutcome>({
              response: null,
              error,
            }),
          ),
        ),

      // Powers the Opening Exposure row shown by the Repayment Plan (2645)
      // and Disbursement & Repayment Summary (2646) report sections, the
      // same way the live Repayment / DisbursementRepaymentSummary
      // components source it. A failure here is not fatal to the report.
      openingExposure: this.creditService
        .getPreviousMaxExposure(this.creditPerposal)
        .pipe(catchError(() => of(null))),
      existingRepayments: this.creditService
        .getExistingRepayment(this.creditPerposal)
        .pipe(catchError(() => of(null))),
      existingDisbursements: this.creditService
        .getExistingDisbursement(this.creditPerposal)
        .pipe(catchError(() => of(null))),
      financialAnalysisSummary: this.creditService
        .getFinancialAnalysis(this.creditPerposal)
        .pipe(catchError(() => of(null))),
      savedDisbursements: this.creditService
        .getProposedDisbursements(this.creditPerposal)
        .pipe(catchError(() => of(null))),
      savedRepayments: this.creditService
        .getProposedRepayments(this.creditPerposal)
        .pipe(catchError(() => of(null))),
      // Tentative Disbursements and Repayment Plan rows (one table per repayment term type).
      summaryPlan: this.creditService
        .getProposedDisbursementRepayment(this.creditPerposal)
        .pipe(catchError(() => of(null))),
      // Repayment terms added by the user — listed on the Word cover page.
      termParameters: this.creditService
        .getCreditTermParameters(this.creditPerposal)
        .pipe(catchError(() => of(null))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ sectionOutcome, openingExposure, existingRepayments, existingDisbursements, financialAnalysisSummary, savedDisbursements, savedRepayments, summaryPlan, termParameters }) => {
        this.repaymentTermCodes =
          this.extractRepaymentTermCodes(termParameters);

        this.savedRepaymentTrancheRows =
          buildRepaymentTrancheRowsFromSaved(
            savedDisbursements,
            savedRepayments,
          );

        this.openingExposure =
          extractBaseExposure(openingExposure) ?? 0;

        this.disbursementSummaryGroups =
          this.buildDisbursementSummaryGroups(summaryPlan);

        this.existingRepayments =
          extractExistingRepayment(existingRepayments) ?? 0;
        this.existingDisbursements =
          extractExistingDisbursement(existingDisbursements) ?? 0;

        const summaryRows = Array.isArray(financialAnalysisSummary)
          ? (financialAnalysisSummary as FinancialAnalysisReportRow[])
          : [];

        this.financialAnalysisSummaryRows = summaryRows
          .slice()
          .sort(
            (first, second) =>
              first.sortOrder - second.sortOrder ||
              first.fncTypSeq - second.fncTypSeq ||
              first.fncValSeq - second.fncValSeq,
          );

        this.financialAnalysisSummaryColumns = this.derivePeriodColumns(
          this.financialAnalysisSummaryRows.map(
            (row) => row.periodValues ?? {},
          ),
        );
        const titleMap = this.buildTitleMap(
          this.pickListService.getOptionsSync(
            PKL_GRP.SUMMARY_TITLE,
          ),
        );

        if (sectionOutcome.error) {
          this.loading = false;

          this.errorMessage =
            (sectionOutcome.error as any)?.error
              ?.message ??
            'CREDIT_REPORT.LOAD_SECTIONS_FAILED';

          console.error(
            'Failed to load Credit Proposal sections:',
            sectionOutcome.error,
          );

          return;
        }

        const rawItems = this.extractSectionResponse(
          sectionOutcome.response,
        );

        this.allSectionData = rawItems
          .map((item) => this.normalizeSectionBlock(item))
          .filter(
            (block): block is CreditSectionBlock =>
              block !== null,
          );

        const createdSections = this.createReportSections(
          this.allSectionData,
          titleMap,
        ).filter(
          (section) =>
            Number(section.refValSeq) !==
            this.OLD_INSTITUTIONAL_HIGHLIGHTS_REF_VAL_SEQ,
        );

        this.reportSections = this.moveSectionAfter(
          createdSections,
          this.FINANCIAL_ANALYSIS_DETAILS_REF_VAL_SEQ,
          this.COMPANY_INTRODUCTION_REF_VAL_SEQ,
        );

        // Same "a)" … "g)" / "1." heading labels the Word report prints.
        this.sectionHeadingLabels = this.exportService.getSectionHeadingLabels(
          this.reportSections,
        );

        this.loading = false;

        // Board of Directors (Annexure-III (a)) is sourced from the
        // onboarding APIs keyed by crptOnbrdngSeq, which is carried on every
        // section block.
        const crptOnbrdngSeq = this.allSectionData
          .map((block) => Number(block.crptOnbrdngSeq ?? 0))
          .find((seq) => seq > 0);

        this.loadDocumentsPreview();

        if (crptOnbrdngSeq && crptOnbrdngSeq > 0) {
          this.loadBoardOfDirectors(crptOnbrdngSeq);
        } else {
          // TEMP DEBUG — no onboarding seq on any section block.
          console.warn(
            '[BoardKeyMgmt] crptOnbrdngSeq not found on section blocks; Board/Key-Management tables cannot load.',
            this.allSectionData.map((block) => block.crptOnbrdngSeq),
          );
        }

        const fallbackTitleSections = this.reportSections
          .filter(
            (section) =>
              section.title === `Section ${section.refValSeq}`,
          )
          .map((section) => section.refValSeq);

        if (fallbackTitleSections.length > 0) {
          console.warn(
            'No Picklist Group 28 or template title found for refValSeq:',
            fallbackTitleSections,
          );
        }
      });
  }

  /* ====================================================== */
  /* DOWNLOAD AS WORD (.docx)                                */
  /* Produces a document that mirrors this on-screen layout   */
  /* ====================================================== */

  async downloadWordReport(): Promise<void> {
    if (
      this.exporting ||
      this.loading ||
      this.reportSections.length === 0
    ) {
      return;
    }

    this.exporting = true;
    this.errorMessage = '';

    try {
      // Capture the six on-screen report tables as images (html2canvas) so
      // they are embedded in the Word document exactly as rendered, instead
      // of being rebuilt as native docx tables. A capture failure for any
      // table simply falls back to that table's native docx rendering.
      const tableImages = await this.captureReportTableImages();
      const { organogram, attachments } =
        await this.loadCreditProposalDocuments();

      await this.exportService.exportToWord(
        this.reportSections,
        `Credit-Proposal-${this.creditPerposal}`,
        {
          tableImages,
          boardOfDirectors: this.boardOfDirectors,
          keyManagement: this.keyManagement,
          committees: this.committees,
          organogram,
          attachments,

          institutionName: this.institutionName,

          coverPage: {
            facilities: this.getCoverFacilities(),
            // Current month and year, e.g. "October 2026".
            dateLabel: new Date().toLocaleDateString('en-US', {
              month: 'long',
              year: 'numeric',
            }),
          },

          openingExposure:
            this.openingExposure,

          repaymentPlanData: this.getCurrentRepaymentPlanData(),

          executiveSummaryTitle:
            'Executive Summary',

          highlightsTitle:
            'Highlights\n(September 2024)',

          highlightsRefValSeq: 2533,

          highlightsColumnPercent: 19,

          executiveSummaryRefValSeqs: [
            2519, // Company Introduction
            2647, // Institutional Highlights
            2521, // Financial Analysis
            2522, // Rating on Institutional Rating System
            // 2526 (Score on Institutional Rating System) is printed in the
            // main body right after Introduction, not in the Executive Summary.
            2523, // Key Risks and Responses
          ],

          financialAnalysisSummaryRows:
            this.financialAnalysisSummaryRows,
          financialAnalysisSummaryColumns:
            this.financialAnalysisSummaryColumns,
        },
      );
    } catch (error) {
      console.error(
        'Failed to export Credit Proposal report:',
        error,
      );

      this.errorMessage =
        'CREDIT_REPORT.EXPORT_FAILED';
    } finally {
      this.exporting = false;
      this.cdr.detectChanges();
    }
  }

  /* ====================================================== */
  /* PROFILE OF BOARD OF DIRECTORS (Annexure-III (a))        */
  /* ====================================================== */

  private loadBoardOfDirectors(crptOnbrdngSeq: number): void {
    // TEMP DEBUG — remove once the Board/Key-Management tables render.
    console.log('[BoardKeyMgmt] crptOnbrdngSeq used for APIs:', crptOnbrdngSeq);

    forkJoin({
      // prsnType = 2 → board of directors.
      directors: this.onboardingService
        .getCorporatePersons(crptOnbrdngSeq, 2)
        .pipe(catchError((error) => {
          console.error('[BoardKeyMgmt] directors (prsnType=2) failed:', error);
          return of([]);
        })),
      // prsnType = 3 → key management.
      keyManagement: this.onboardingService
        .getCorporatePersons(crptOnbrdngSeq, 3)
        .pipe(catchError((error) => {
          console.error('[BoardKeyMgmt] keyManagement (prsnType=3) failed:', error);
          return of([]);
        })),
      committees: this.onboardingService
        .getAllCommittees(crptOnbrdngSeq)
        .pipe(catchError((error) => {
          console.error('[BoardKeyMgmt] committees failed:', error);
          return of([]);
        })),
      // Person fields are saved as picklist codes; the labels come from here.
      picklists: this.pickListService
        .loadCodesAsync([
          PKL_GRP.DESIGNATION,
          PKL_GRP.POSITION,
          PKL_GRP.QUALIFICATION,
        ])
        .pipe(catchError(() => of(null))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ directors, keyManagement, committees }) => {
        // TEMP DEBUG — raw API payloads and derived row counts.
        console.log('[BoardKeyMgmt] raw directors response:', directors);
        console.log('[BoardKeyMgmt] raw keyManagement response:', keyManagement);
        console.log('[BoardKeyMgmt] raw committees response:', committees);

        this.boardOfDirectors = this.buildBoardOfDirectors(
          directors,
          committees,
        );
        this.keyManagement = this.buildKeyManagement(keyManagement);
        this.committees = this.buildCommittees(committees);

        console.log(
          '[BoardKeyMgmt] built rows → boardOfDirectors:',
          this.boardOfDirectors.length,
          'keyManagement:',
          this.keyManagement.length,
        );
      });
  }

  private buildBoardOfDirectors(
    personsResponse: unknown,
    committeesResponse: unknown,
  ): BoardDirectorReportRow[] {
    const persons = this.asArray(personsResponse);
    const committees = this.asArray(committeesResponse);

    // Map each person (by prsnSeq) to the committee name(s) they belong to.
    // prsnSeq is the reliable per-person key shared by both APIs (clntSeq is
    // the same client id across every person, so it cannot distinguish them).
    const committeesByPerson = new Map<number, string[]>();

    committees.forEach((committee: any) => {
      const committeeName = String(committee?.cmtNm ?? '').trim();
      const members = this.asArray(committee?.members);

      members.forEach((member: any) => {
        const prsnSeq = Number(member?.prsnSeq ?? 0);
        if (!prsnSeq || Number(member?.delFlg ?? 0) === 1) {
          return;
        }

        const existing = committeesByPerson.get(prsnSeq) ?? [];
        if (committeeName && !existing.includes(committeeName)) {
          existing.push(committeeName);
        }
        committeesByPerson.set(prsnSeq, existing);
      });
    });

    return persons
      .filter((person: any) => Number(person?.delFlg ?? 0) !== 1)
      .map((person: any) => {
        const prsnSeq = Number(person?.prsnSeq ?? 0);
        const committeeNames = committeesByPerson.get(prsnSeq) ?? [];

        return {
          name: String(person?.flNm ?? '').trim(),
          position: this.personPosition(person),
          qualification: this.personQualification(person),
          committee: committeeNames.join(', '),
          experience: this.personExperience(person),
        };
      });
  }

  /** Loads the Organogram and Attachments shown in the on-screen report. */
  private loadDocumentsPreview(): void {
    this.loadCreditProposalDocuments()
      .then(({ organogram, attachments }) => {
        this.organogram = organogram;
        this.attachments = attachments;
        this.cdr.detectChanges();
      })
      .catch((error) => {
        console.error('Credit Proposal documents load error:', error);
      });
  }

  /**
   * Documents uploaded on the Credit Proposal's Documents tab (entity type
   * 12), fetched for the on-screen report and again on each export:
   *  - organogram:  latest Document tab upload (docSeq 43) when it is a
   *                 PNG/JPEG image, for Annexure-III (d);
   *  - attachments: every Attachments tab upload, titled by its document
   *                 type, for the "Attachments" section after it.
   * A failed request just leaves them out — the report still exports.
   */
  private async loadCreditProposalDocuments(): Promise<{
    organogram: OrganogramReportImage | null;
    attachments: AttachmentReportItem[];
  }> {
    const { docs, docTypes } = await firstValueFrom(
      forkJoin({
        docs: this.leadService
          .getLeadDocs(this.creditPerposal, MODULE_SEQ.CREDIT_PROPOSAL)
          .pipe(catchError(() => of(null))),
        docTypes: this.setup
          .getAllDocumentByCategry(MODULE_SEQ.CREDIT_PROPOSAL)
          .pipe(catchError(() => of([]))),
      }),
    );

    const docTypeLabels = new Map<number, string>(
      this.asArray(docTypes).map((item: any) => [
        Number(item?.docSeq),
        String(item?.docNm ?? '').trim(),
      ]),
    );

    // Newest first, so the organogram is the latest upload.
    const uploaded = this.asArray(docs)
      .filter((doc: any) => !doc?.delFlg)
      .sort(
        (first: any, second: any) =>
          Number(second?.entyDocSeq ?? 0) - Number(first?.entyDocSeq ?? 0),
      );

    const organogramDoc = uploaded.find((doc: any) =>
      Number(doc?.docTyp) === CREDIT_PROPOSAL_ORGANOGRAM_DOC_SEQ,
    );

    const attachments = uploaded
      .filter((doc: any) =>
        CREDIT_PROPOSAL_REPORT_ATTACHMENT_DOC_SEQS.includes(Number(doc?.docTyp)),
      )
      // Oldest first, in the order the attachment types are listed.
      .reverse()
      .sort(
        (first: any, second: any) =>
          CREDIT_PROPOSAL_REPORT_ATTACHMENT_DOC_SEQS.indexOf(Number(first.docTyp)) -
          CREDIT_PROPOSAL_REPORT_ATTACHMENT_DOC_SEQS.indexOf(Number(second.docTyp)),
      )
      .map((doc: any) => ({
        title:
          docTypeLabels.get(Number(doc.docTyp)) ||
          String(doc?.docTypNm ?? '').trim(),
        fileName: String(doc?.docNm ?? '').trim(),
        image: this.toReportImage(doc),
      }));

    return {
      organogram: organogramDoc ? this.toReportImage(organogramDoc) : null,
      attachments,
    };
  }

  /** The uploaded file as a report image when it is a PNG/JPEG image; otherwise null. */
  private toReportImage(doc: any): OrganogramReportImage | null {
    if (!doc?.docImg) {
      return null;
    }

    const base64 = cleanBase64(doc.docImg);
    const { mimeType } = getFileInfo(base64, doc.docNm ?? '');

    if (mimeType !== 'image/png' && mimeType !== 'image/jpeg') {
      return null;
    }

    return {
      dataUrl: `data:${mimeType};base64,${base64}`,
      type: mimeType === 'image/png' ? 'png' : 'jpg',
    };
  }

  /** Committees with their active members, for the Committee Details table. */
  private buildCommittees(
    committeesResponse: unknown,
  ): CommitteeReportRow[] {
    return this.asArray(committeesResponse)
      .filter((committee: any) => Number(committee?.delFlg ?? 0) !== 1)
      .map((committee: any) => ({
        committee: String(committee?.cmtNm ?? '').trim(),
        members: this.asArray(committee?.members)
          .filter((member: any) => Number(member?.delFlg ?? 0) !== 1)
          .map((member: any) => ({
            name: String(member?.flNm ?? '').trim(),
            position: this.personPosition(member),
            qualification: this.personQualification(member),
          })),
      }))
      .filter((committee) => committee.committee || committee.members.length > 0);
  }

  private buildKeyManagement(
    personsResponse: unknown,
  ): KeyManagementReportRow[] {
    return this.asArray(personsResponse)
      .filter((person: any) => Number(person?.delFlg ?? 0) !== 1)
      .map((person: any) => ({
        position: this.personPosition(person),
        name: String(person?.flNm ?? '').trim(),
        qualification: this.personQualification(person),
        experience: this.personExperience(person),
      }));
  }

  /*
   * The Directors / Key Management popups share one form (Designation +
   * Current Profession Experience). Persons saved before that form keep
   * their older Position / Qualification / Area of Expertise values, which
   * are still shown when the current field is empty.
   */

  private personPosition(person: any): string {
    return (
      this.pickListLabel(PKL_GRP.DESIGNATION, person?.dsgntn) ||
      this.pickListLabel(PKL_GRP.POSITION, person?.pstn)
    );
  }

  private personQualification(person: any): string {
    return this.pickListLabel(PKL_GRP.QUALIFICATION, person?.qlfctn);
  }

  private personExperience(person: any): string {
    return (
      String(person?.crntPrfssnExp ?? '').trim() ||
      String(person?.arOfExprtse ?? '').trim()
    );
  }

  /** Label of a saved picklist code; '' when unset (0) or no longer in the picklist. */
  private pickListLabel(group: number, code: unknown): string {
    const value = Number(code ?? 0);

    if (!Number.isFinite(value) || value <= 0) {
      return '';
    }

    const option = this.pickListService
      .getOptionsSync(group)
      .find((candidate) => Number(candidate.value) === value);

    return String(option?.label ?? '').trim();
  }

  /**
   * Normalizes an API response into an array. Handles a bare array as well as
   * common envelope shapes ({ data }, { content }, { result(s) }, { items },
   * { list }, { records }, and one level of nesting such as { data: { content } }).
   */
  private asArray(value: unknown): any[] {
    if (Array.isArray(value)) {
      return value;
    }

    const container = value as Record<string, any> | null | undefined;
    if (!container || typeof container !== 'object') {
      return [];
    }

    const keys = ['data', 'content', 'result', 'results', 'items', 'list', 'records'];

    for (const key of keys) {
      const candidate = container[key];
      if (Array.isArray(candidate)) {
        return candidate;
      }
      if (candidate && typeof candidate === 'object' && Array.isArray(candidate.content)) {
        return candidate.content;
      }
    }

    return [];
  }

  /* ====================================================== */
  /* ON-SCREEN TABLE SNAPSHOTS (html2canvas)                 */
  /* The six specially-styled report tables are captured as   */
  /* images from the live DOM and embedded in the Word doc.   */
  /* ====================================================== */

  /** DOM markers (data-export-table) for the six tables that are snapshotted. */
  private static readonly EXPORT_TABLE_KEYS: CapturedTableKey[] = [
    'financial-analysis-details',
    'repayment-plan',
    'disbursement-summary',
    'institutional-rating',
    'recommendation',
    'financial-analysis-summary',
  ];

  /** Temporary class giving the Financial Analysis snapshot its compact report layout. */
  private static readonly FINANCIAL_ANALYSIS_EXPORT_CLASS = 'fa-export-compact';

  /**
   * Width / height of the room the Financial Analysis snapshot gets in Word:
   * portrait page, 0.4in margins, below the section title and Annexure
   * heading (about 739 x 847 px), so the snapshot fills both its width and
   * its height.
   */
  private static readonly FINANCIAL_ANALYSIS_PAGE_RATIO = 0.87;

  private async captureReportTableImages(): Promise<CapturedTableImages> {
    const images: CapturedTableImages = {};

    if (typeof document === 'undefined') {
      return images;
    }

    let html2canvas: typeof import('html2canvas').default;

    try {
      // Lazy import so html2canvas only downloads when a report is exported.
      html2canvas = (await import('html2canvas')).default;
    } catch (error) {
      console.error('Unable to load html2canvas for table capture:', error);
      return images;
    }

    // html2canvas measures font baselines in the live document (its
    // FontMetrics uses the global `document`, not the clone), so the fix has
    // to be on the page itself while capturing.
    const baselineFix = this.addTextBaselineFix(document);

    try {
      for (const key of GenrateCreditReport.EXPORT_TABLE_KEYS) {
        await this.captureTablesForKey(html2canvas, key, images);
      }
    } finally {
      baselineFix.remove();
    }

    return images;
  }

  private async captureTablesForKey(
    html2canvas: typeof import('html2canvas').default,
    key: CapturedTableKey,
    images: CapturedTableImages,
  ): Promise<void> {
    // A key can mark several tables (the Repayment Plan has one per
    // repayment term type); each is captured as its own image.
    const elements = Array.from(
      document.querySelectorAll<HTMLElement>(`[data-export-table="${key}"]`),
    );
    const captured: CapturedTableImage[] = [];

    for (const [elementIndex, element] of elements.entries()) {
      // Financial Analysis is snapshotted in the compact, Excel-like layout
      // of the reference report (tight rows, fixed narrow width) so the
      // whole table fits on one page and stays readable. The class is only
      // applied for the duration of the capture; the screen is unchanged.
      const compactClass =
        key === 'financial-analysis-summary'
          ? GenrateCreditReport.FINANCIAL_ANALYSIS_EXPORT_CLASS
          : null;

      if (compactClass) {
        element.classList.add(compactClass);
        this.fitFinancialAnalysisExportWidth(element);
      }

      try {
        const capture = () =>
          html2canvas(element, {
            // Financial Analysis is shrunk to fit one page, so it is captured
            // at a higher resolution to keep the small text sharp in Word.
            scale: compactClass ? 4 : 2,
            backgroundColor: '#ffffff',
            useCORS: true,
            logging: false,
            // Capture the table at its full natural size, even when it is
            // horizontally scrolled inside its wrapper on screen.
            width: element.scrollWidth,
            height: element.scrollHeight,
            windowWidth: Math.max(
              document.documentElement.scrollWidth,
              element.scrollWidth + 64,
            ),
            onclone: (clonedDoc: Document) => {
              this.fixTextBaselineForCapture(clonedDoc);
              this.normalizeColorsForCapture(clonedDoc, key, elementIndex);

              if (compactClass) {
                this.applyExportWidthToClone(clonedDoc, key, elementIndex, element);
              }
            },
          });

        // Compact snapshots are trimmed to the table itself, so a canvas
        // wider than what was actually drawn never adds blank space.
        const captureTrimmed = async () =>
          compactClass
            ? this.trimCanvasWhitespace(await capture())
            : capture();

        let canvas = await captureTrimmed();

        // The Word page is filled by height, so a snapshot narrower than
        // the page's proportion leaves empty space on both sides. Widen
        // the table by what the actual snapshot is short of and capture
        // again (widening also unwraps header labels, hence a few passes).
        if (compactClass) {
          for (let pass = 0; pass < 3; pass++) {
            const ratio = canvas.width / canvas.height;

            if (ratio >= GenrateCreditReport.FINANCIAL_ANALYSIS_PAGE_RATIO * 0.98) {
              break;
            }

            this.setExportWidth(
              element,
              Math.round(
                element.offsetWidth *
                  (GenrateCreditReport.FINANCIAL_ANALYSIS_PAGE_RATIO / ratio),
              ),
            );
            canvas = await captureTrimmed();
          }
        }

        const rect = element.getBoundingClientRect();

        const image: CapturedTableImage = {
          dataUrl: canvas.toDataURL('image/png'),
          width: canvas.width,
          height: canvas.height,
          cssWidth: Math.round(
            Math.max(rect.width, element.scrollWidth) || element.offsetWidth,
          ),
          cssHeight: Math.round(
            Math.max(rect.height, element.scrollHeight) || element.offsetHeight,
          ),
          ...this.measureTableLayout(element, canvas.height),
        };

        captured.push(image);
      } catch (error) {
        console.error(`Unable to capture report table "${key}":`, error);
      } finally {
        if (compactClass) {
          element.classList.remove(compactClass);
          element.style.removeProperty('width');
          element.style.removeProperty('min-width');
          element.style.removeProperty('max-width');
        }
      }
    }

    if (captured.length === 1) {
      images[key] = captured[0];
    } else if (captured.length > 1) {
      images[key] = captured;
    }
  }

  /**
   * Widens the compact Financial Analysis table to the page's width/height
   * proportion. The table is scaled to fit one page by its height, so a
   * table narrower than the page proportion leaves wide empty margins on
   * both sides in Word; matching the proportion makes it fill the page
   * width instead.
   */
  private fitFinancialAnalysisExportWidth(element: HTMLElement): void {
    const minWidth = 760;

    let width = minWidth;

    // Widening lets header labels unwrap and shortens the table, so settle
    // the width over a couple of passes.
    for (let pass = 0; pass < 3; pass++) {
      const target = Math.max(
        minWidth,
        Math.round(
          element.offsetHeight * GenrateCreditReport.FINANCIAL_ANALYSIS_PAGE_RATIO,
        ),
      );

      if (target === width && pass > 0) {
        break;
      }

      width = target;
      this.setExportWidth(element, width);
    }
  }

  /**
   * Gives the cloned table the same export width as the live one and lets
   * it overflow its (scrolling, max-width) wrappers, so html2canvas lays the
   * table out at that width instead of leaving the extra width blank.
   */
  private applyExportWidthToClone(
    clonedDoc: Document,
    key: CapturedTableKey,
    elementIndex: number,
    liveElement: HTMLElement,
  ): void {
    const cloneRoot = clonedDoc.querySelectorAll<HTMLElement>(
      `[data-export-table="${key}"]`,
    )[elementIndex];

    if (!cloneRoot) {
      return;
    }

    this.setExportWidth(cloneRoot, liveElement.offsetWidth);

    for (
      let parent = cloneRoot.parentElement;
      parent && parent !== clonedDoc.body;
      parent = parent.parentElement
    ) {
      parent.style.setProperty('overflow', 'visible', 'important');
      parent.style.setProperty('max-width', 'none', 'important');
    }
  }

  /** Crops the blank (near-white) right and bottom edges off a snapshot. */
  private trimCanvasWhitespace(canvas: HTMLCanvasElement): HTMLCanvasElement {
    const context = canvas.getContext('2d');

    if (!context || !canvas.width || !canvas.height) {
      return canvas;
    }

    const { data, width, height } = context.getImageData(
      0,
      0,
      canvas.width,
      canvas.height,
    );
    const isInk = (index: number) =>
      data[index] < 240 || data[index + 1] < 240 || data[index + 2] < 240;

    let right = 0;
    let bottom = 0;

    for (let y = 0; y < height; y++) {
      for (let x = width - 1; x >= 0; x--) {
        if (isInk((y * width + x) * 4)) {
          right = Math.max(right, x);
          bottom = y;
          break;
        }
      }
    }

    const croppedWidth = Math.min(width, right + 3);
    const croppedHeight = Math.min(height, bottom + 3);

    if (croppedWidth >= width && croppedHeight >= height) {
      return canvas;
    }

    const cropped = document.createElement('canvas');
    cropped.width = croppedWidth;
    cropped.height = croppedHeight;
    cropped.getContext('2d')?.drawImage(canvas, 0, 0);

    return cropped;
  }

  private setExportWidth(element: HTMLElement, width: number): void {
    element.style.setProperty('width', `${width}px`, 'important');
    element.style.setProperty('min-width', `${width}px`, 'important');
    element.style.setProperty('max-width', `${width}px`, 'important');
  }

  /**
   * Header height and body row bottoms of a captured table, in canvas px, so
   * the Word export can repeat the header on every continuation page and
   * break pages between rows.
   */
  private measureTableLayout(
    element: HTMLElement,
    canvasHeight: number,
  ): Pick<CapturedTableImage, 'headerHeight' | 'rowBreaks'> {
    const cssHeight = element.scrollHeight || element.offsetHeight;

    if (!cssHeight || !canvasHeight) {
      return {};
    }

    const scale = canvasHeight / cssHeight;
    const top = element.getBoundingClientRect().top;
    const toCanvasPx = (cssY: number) => Math.round((cssY - top) * scale);

    const header = element.querySelector<HTMLElement>(':scope > thead');
    const rows = Array.from(
      element.querySelectorAll<HTMLElement>(':scope > tbody > tr'),
    );

    return {
      headerHeight: header
        ? toCanvasPx(header.getBoundingClientRect().bottom)
        : undefined,
      rowBreaks: rows.map((row) =>
        toCanvasPx(row.getBoundingClientRect().bottom),
      ),
    };
  }

  /**
   * Tailwind's preflight sets `img { display: block }`, which makes
   * html2canvas 1.4.1 mis-measure the text baseline: every line of text is
   * drawn shifted down, so in tight table rows its lower half is cut off.
   * Restoring the default inline display fixes it.
   */
  private fixTextBaselineForCapture(clonedDoc: Document): void {
    this.addTextBaselineFix(clonedDoc);
  }

  private addTextBaselineFix(target: Document): HTMLStyleElement {
    const style = target.createElement('style');
    style.textContent = 'img { display: inline-block !important; }';
    target.head?.appendChild(style);
    return style;
  }

  /**
   * html2canvas 1.4.1 cannot parse modern CSS color functions (oklch/oklab)
   * that Tailwind v4 emits. Before capture, copy the browser-resolved
   * (rgb) colors from the live element onto the cloned element so every
   * color html2canvas reads is one it understands. The live tree and the
   * clone share identical structure/order, so they can be paired by index.
   */
  private normalizeColorsForCapture(
    clonedDoc: Document,
    key: CapturedTableKey,
    elementIndex = 0,
  ): void {
    const selector = `[data-export-table="${key}"]`;
    const liveRoot =
      document.querySelectorAll<HTMLElement>(selector)[elementIndex];
    const cloneRoot =
      clonedDoc.querySelectorAll<HTMLElement>(selector)[elementIndex];

    if (!liveRoot || !cloneRoot) {
      return;
    }

    const liveEls: HTMLElement[] = [
      liveRoot,
      ...Array.from(liveRoot.querySelectorAll<HTMLElement>('*')),
    ];
    const cloneEls: HTMLElement[] = [
      cloneRoot,
      ...Array.from(cloneRoot.querySelectorAll<HTMLElement>('*')),
    ];

    const count = Math.min(liveEls.length, cloneEls.length);

    for (let index = 0; index < count; index++) {
      const computed = window.getComputedStyle(liveEls[index]);
      const style = cloneEls[index].style;

      style.color = computed.color;
      style.backgroundColor = computed.backgroundColor;
      style.borderTopColor = computed.borderTopColor;
      style.borderRightColor = computed.borderRightColor;
      style.borderBottomColor = computed.borderBottomColor;
      style.borderLeftColor = computed.borderLeftColor;
    }
  }

  /* ====================================================== */
  /* RESPONSE / BLOCK NORMALIZATION */
  /* ====================================================== */

  private extractSectionResponse(
    response: unknown,
  ): unknown[] {
    const candidate = response as
      | {
          data?: {
            content?: unknown;
            crdtPrpslBlkList?: unknown;
            blockList?: unknown;
          };
          content?: unknown;
          crdtPrpslBlkList?: unknown;
          blockList?: unknown;
        }
      | null
      | undefined;

    const possibleLists: unknown[] = [
      response,
      candidate?.data,
      candidate?.content,
      candidate?.data?.content,
      candidate?.crdtPrpslBlkList,
      candidate?.data?.crdtPrpslBlkList,
      candidate?.blockList,
      candidate?.data?.blockList,
    ];

    const matchedList = possibleLists.find(
      (value): value is unknown[] =>
        Array.isArray(value),
    );

    return matchedList ?? [];
  }

  private normalizeSectionBlock(
    item: unknown,
  ): CreditSectionBlock | null {
    if (!item || typeof item !== 'object') {
      return null;
    }

    const raw = item as Record<string, unknown>;

    if (Number(raw['delFlg'] ?? 0) === 1) {
      return null;
    }

    const txtData = raw['txtData'];

    return {
      blkTyp: Number(raw['blkTyp'] ?? 0),
      crdtPrpslBlkSeq: Number(raw['crdtPrpslBlkSeq'] ?? 0),
      crdtPrpslSeq: Number(raw['crdtPrpslSeq'] ?? 0),
      crptOnbrdngSeq: Number(raw['crptOnbrdngSeq'] ?? 0),
      delFlg: Number(raw['delFlg'] ?? 0),
      refValSeq: Number(raw['refValSeq'] ?? 0),
      sortOrdr: Number(raw['sortOrdr'] ?? 0),

      txtData:
        txtData !== null && txtData !== undefined
          ? String(txtData).trim()
          : null,

      jsonData: this.normalizeJsonData(raw['jsonData']),

      crtdDt: String(raw['crtdDt'] ?? ''),
      lastUpdDt: (raw['lastUpdDt'] as string | null) ?? null,
    };
  }

  /* ====================================================== */
  /* PICKLIST TITLES: GROUP 28 */
  /* ====================================================== */

  private buildTitleMap(
    options: DropdownOption[],
  ): Record<number, string> {
    const titleMap: Record<number, string> = {};

    if (!Array.isArray(options)) {
      return titleMap;
    }

    options
      .filter(
        (option) =>
          option.original?.refCdActvFlg !== false &&
          Number(option.original?.delFlg ?? 0) !== 1,
      )
      .forEach((option) => {
        const refValSeq = Number(option.value);
        const title = String(option.label ?? '').trim();

        if (
          !Number.isFinite(refValSeq) ||
          refValSeq <= 0 ||
          !title
        ) {
          return;
        }

        titleMap[refValSeq] = title;
      });

    return titleMap;
  }

  private resolveSectionTitle(
    refValSeq: number,
    metadata: ReportTemplateMetadata | undefined,
    titleMap: Record<number, string>,
  ): string {
    if (
      Number(refValSeq) ===
      this.FINANCIAL_ANALYSIS_DETAILS_REF_VAL_SEQ
    ) {
      return 'Institutional Highlights';
    }

    const picklistTitle = String(
      titleMap[refValSeq] ?? '',
    ).trim();

    if (picklistTitle) {
      return this.fixTitleSpelling(picklistTitle);
    }

    const templateTitle = String(
      metadata?.templateTitle ?? '',
    ).trim();

    if (templateTitle) {
      return this.fixTitleSpelling(templateTitle);
    }

    return `Section ${refValSeq}`;
  }

  /** Corrects "Disbursment" → "Disbursement" in titles saved with the typo. */
  private fixTitleSpelling(title: string): string {
    return title.replace(/\bdisbursment/gi, (word) =>
      word[0] === 'D' ? 'Disbursement' : 'disbursement',
    );
  }

  /* ====================================================== */
  /* CREATE REPORT SECTIONS — template controls order        */
  /* ====================================================== */

  private createReportSections(
    blocks: CreditSectionBlock[],
    titleMap: Record<number, string>,
  ): ReportSection[] {
    const sectionMap = new Map<number, CreditSectionBlock[]>();

    blocks.forEach((block) => {
      const refValSeq = Number(block.refValSeq);

      if (!Number.isFinite(refValSeq) || refValSeq <= 0) {
        // refValSeq <= 0 belongs to the API-driven builder
        // sections rendered outside this block-based pipeline.
        return;
      }

      const existingBlocks = sectionMap.get(refValSeq) ?? [];
      existingBlocks.push(block);
      sectionMap.set(refValSeq, existingBlocks);
    });

    return Array.from(sectionMap.entries())
      .map(([refValSeq, sectionBlocks]): ReportSection => {
        const metadata = this.templateMetadata.get(refValSeq);

        const sortedBlocks = [...sectionBlocks].sort(
          (first, second) =>
            Number(first.sortOrdr) - Number(second.sortOrdr) ||
            Number(first.crdtPrpslBlkSeq) -
              Number(second.crdtPrpslBlkSeq),
        );

        const templateOrder =
          metadata?.templateOrder ?? Number.MAX_SAFE_INTEGER;

        return {
          sectionId: metadata?.sectionId,
          type: metadata?.type,
          refValSeq,
          title: this.resolveSectionTitle(
            refValSeq,
            metadata,
            titleMap,
          ),
          templateOrder,
          sectionOrder: templateOrder,
          blocks: sortedBlocks,
        };
      })
      .sort((first, second) => {
        if (first.templateOrder !== second.templateOrder) {
          return first.templateOrder - second.templateOrder;
        }

        const firstMinSortOrdr = this.getMinSortOrdr(first);
        const secondMinSortOrdr = this.getMinSortOrdr(second);

        if (firstMinSortOrdr !== secondMinSortOrdr) {
          return firstMinSortOrdr - secondMinSortOrdr;
        }

        if (first.refValSeq !== second.refValSeq) {
          return first.refValSeq - second.refValSeq;
        }

        return (
          this.getMinBlockSeq(first) -
          this.getMinBlockSeq(second)
        );
      });
  }

  private getMinSortOrdr(section: ReportSection): number {
    return section.blocks.reduce(
      (min, block) => Math.min(min, Number(block.sortOrdr)),
      Number.MAX_SAFE_INTEGER,
    );
  }

  private getMinBlockSeq(section: ReportSection): number {
    return section.blocks.reduce(
      (min, block) =>
        Math.min(min, Number(block.crdtPrpslBlkSeq)),
      Number.MAX_SAFE_INTEGER,
    );
  }

  /**
   * Moves the section matching `refValSeq` immediately after the section
   * matching `targetRefValSeq`, while preserving every other section's
   * relative order. A no-op if either section is missing.
   */
  private moveSectionAfter(
    sections: ReportSection[],
    refValSeq: number,
    targetRefValSeq: number,
  ): ReportSection[] {
    const currentIndex = sections.findIndex(
      (section) => Number(section.refValSeq) === refValSeq,
    );

    const targetIndex = sections.findIndex(
      (section) =>
        Number(section.refValSeq) === targetRefValSeq,
    );

    if (currentIndex === -1 || targetIndex === -1) {
      return sections;
    }

    const reordered = [...sections];
    const [targetSection] = reordered.splice(currentIndex, 1);

    const adjustedTargetIndex = reordered.findIndex(
      (section) =>
        Number(section.refValSeq) === targetRefValSeq,
    );

    reordered.splice(
      adjustedTargetIndex + 1,
      0,
      targetSection,
    );

    return reordered;
  }

  /* ====================================================== */
  /* JSON NORMALIZATION */
  /* ====================================================== */

  private normalizeJsonData(
    value: unknown,
  ): SectionJsonData[] {
    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return [];
    }

    if (Array.isArray(value)) {
      return value.filter(
        (
          item: unknown,
        ): item is SectionJsonData =>
          item !== null &&
          typeof item === 'object',
      );
    }

    if (typeof value === 'object') {
      return [
        value as SectionJsonData,
      ];
    }

    if (typeof value === 'string') {
      const trimmedValue = value.trim();

      if (!trimmedValue) {
        return [];
      }

      try {
        const parsed = JSON.parse(
          trimmedValue,
        );

        return this.normalizeJsonData(
          parsed,
        );
      } catch (error) {
        console.warn(
          'Invalid section jsonData:',
          value,
          error,
        );

        return [];
      }
    }

    return [];
  }

  /* ====================================================== */
  /* REF-VAL-SEQ MAPPED SECTIONS (report-only rendering)     */
  /* Financial Analysis Details (2647), Repayment Plan       */
  /* (2645), Disbursement & Repayment Summary (2646)         */
  /* ====================================================== */

  isFinancialAnalysisDetailsSection(
    section: ReportSection,
  ): boolean {
    return (
      Number(section.refValSeq) ===
      this.FINANCIAL_ANALYSIS_DETAILS_REF_VAL_SEQ
    );
  }

  isRepaymentPlanSection(
    section: ReportSection,
  ): boolean {
    return (
      Number(section.refValSeq) ===
      this.REPAYMENT_PLAN_REF_VAL_SEQ
    );
  }

  isDisbursementRepaymentSummarySection(
    section: ReportSection,
  ): boolean {
    return (
      Number(section.refValSeq) ===
      this.DISBURSEMENT_REPAYMENT_SUMMARY_REF_VAL_SEQ
    );
  }

  /* ---------- Financial Analysis Details (2647) ---------- */

  getFinancialAnalysisDetailsData(
    section: ReportSection,
  ): FinancialAnalysisReportData | null {
    // Same accumulation issue as Repayment Plan / Disbursement & Repayment
    // Summary — only the latest saved block should be rendered.
    const latestBlock = this.getLatestBlock(section);

    if (!latestBlock) {
      return null;
    }

    const rawItem = this.findRawJsonItem(
      latestBlock,
      (candidate) =>
        candidate['type'] ===
          'financial-analysis-details' ||
        Array.isArray(candidate['rows']),
    );

    if (!rawItem) {
      return null;
    }

    const rawRows = Array.isArray(
      rawItem['rows'],
    )
      ? (rawItem['rows'] as unknown[])
      : [];

    const rows: FinancialAnalysisReportRow[] =
      rawRows
        .filter(
          (
            row,
          ): row is Record<string, unknown> =>
            !!row && typeof row === 'object',
        )
        .map((row) => ({
          institution: String(
            row['institution'] ?? '',
          ).trim(),
          sortOrder: this.toNumber(
            row['sortOrder'],
          ),
          fncTypSeq: this.toNumber(
            row['fncTypSeq'],
          ),
          fncValSeq: this.toNumber(
            row['fncValSeq'],
          ),
          head: String(
            row['head'] ?? '',
          ).trim(),
          dscr: String(
            row['dscr'] ?? '',
          ).trim(),
          periodValues:
            this.normalizeNullableAmountRecord(
              row['periodValues'],
            ),
        }))
        .sort(
          (first, second) =>
            first.sortOrder - second.sortOrder ||
            first.fncTypSeq - second.fncTypSeq ||
            first.fncValSeq - second.fncValSeq,
        );

    const rawPeriodColumns = Array.isArray(
      rawItem['periodColumns'],
    )
      ? (rawItem['periodColumns'] as unknown[])
          .map((column) => String(column ?? '').trim())
          .filter(Boolean)
      : [];

    // Only periods that carry a value in at least one row — a year/statement
    // type with no saved figures (e.g. the Unaudited column of a year saved
    // as Audited) is not shown, nor captured into the Word snapshot.
    const periodColumns = (
      rawPeriodColumns.length > 0
        ? rawPeriodColumns
        : this.derivePeriodColumns(
            rows.map((row) => row.periodValues),
          )
    ).filter((period) =>
      rows.some(
        (row) =>
          row.periodValues[period] !== null &&
          row.periodValues[period] !== undefined,
      ),
    );

    const institutionName =
      String(
        rawItem['institutionName'] ?? '',
      ).trim() ||
      rows.find((row) => row.institution)
        ?.institution ||
      '';

    return { institutionName, periodColumns, rows };
  }

  isFirstFinancialAnalysisHeadRow(
    rows: FinancialAnalysisReportRow[],
    index: number,
  ): boolean {
    if (index === 0) {
      return true;
    }

    return (
      this.normalizeForComparison(rows[index]?.head) !==
      this.normalizeForComparison(rows[index - 1]?.head)
    );
  }

  getFinancialAnalysisHeadRowSpan(
    rows: FinancialAnalysisReportRow[],
    index: number,
  ): number {
    if (
      !this.isFirstFinancialAnalysisHeadRow(
        rows,
        index,
      )
    ) {
      return 0;
    }

    const currentHead = this.normalizeForComparison(
      rows[index]?.head,
    );

    let rowSpan = 1;

    for (
      let nextIndex = index + 1;
      nextIndex < rows.length;
      nextIndex++
    ) {
      if (
        this.normalizeForComparison(
          rows[nextIndex]?.head,
        ) !== currentHead
      ) {
        break;
      }

      rowSpan++;
    }

    return rowSpan;
  }

  getFinancialAnalysisPeriodValue(
    row: FinancialAnalysisReportRow,
    period: string,
  ): number | null {
    return row.periodValues?.[period] ?? null;
  }

  /**
   * Institutional Highlights table exactly as the Word report builds it
   * (CreditReportExportService.buildFinancialAnalysisDetails): one
   * Indicators column (description, or head when there is none), periods
   * grouped under "Unaudited" / "Audited Accounts" with the year beneath,
   * and a CAGR column. Null groups → a single header row.
   */
  getInstitutionalHighlightsView(data: FinancialAnalysisReportData): {
    periods: { column: string; label: string }[];
    groups: { label: string; span: number }[] | null;
    showCagr: boolean;
    rows: { indicator: string; values: (number | null)[]; cagr: string }[];
  } {
    const periodColumns = this.exportService.orderHighlightsColumns(data.periodColumns);
    const periods = periodColumns.map((column) => ({
      column,
      ...this.exportService.splitHighlightsPeriod(column),
    }));
    const showCagr = periods.length > 1;

    let groups: { label: string; span: number }[] | null = null;

    if (periods.length > 0 && periods.every((period) => !!period.group)) {
      groups = [];

      for (const period of periods) {
        const last = groups[groups.length - 1];

        if (last && last.label === period.group) {
          last.span++;
        } else {
          groups.push({ label: period.group, span: 1 });
        }
      }
    }

    return {
      periods,
      groups,
      showCagr,
      rows: data.rows.map((row) => {
        const dscr = row.dscr && row.dscr !== '-' ? row.dscr : '';

        return {
          indicator: dscr || row.head || '-',
          values: periodColumns.map((column) =>
            this.getFinancialAnalysisPeriodValue(row, column),
          ),
          cagr: showCagr
            ? this.exportService.highlightsCagr(row, periodColumns, periods)
            : '',
        };
      }),
    };
  }

  /**
   * A "Total ..." head that holds a single row (e.g. head "Total Current
   * Assets" with one value row). It is rendered as one bold total line
   * instead of a heading row followed by an unlabeled value row, which
   * keeps the Financial Analysis table compact enough for one page.
   */
  isFinancialAnalysisTotalGroupRow(
    rows: FinancialAnalysisReportRow[],
    index: number,
  ): boolean {
    const row = rows[index];

    if (!this.normalizeForComparison(row?.head).startsWith('total')) {
      return false;
    }

    const isFirst = this.isFirstFinancialAnalysisHeadRow(rows, index);
    const isLast =
      index === rows.length - 1 ||
      this.normalizeForComparison(rows[index + 1]?.head) !==
        this.normalizeForComparison(row?.head);

    return isFirst && isLast;
  }

  isFinancialAnalysisSubtotalRow(
    row: FinancialAnalysisReportRow,
  ): boolean {
    return this.normalizeForComparison(row?.dscr).startsWith(
      'total',
    );
  }

  /* ---------- Repayment Plan (2645) ---------- */

  getRepaymentPlanData(
    section: ReportSection,
  ): RepaymentPlanReportData | null {
    const latestBlock = this.getLatestBlock(section);

    const snapshotRows =
      latestBlock && Array.isArray(latestBlock.jsonData)
        ? flattenRepaymentTrancheRows(
            latestBlock.jsonData as unknown[],
          )
        : [];

    const sourceRows =
      this.savedRepaymentTrancheRows.length > 0
        ? this.savedRepaymentTrancheRows
        : snapshotRows;

    if (!latestBlock && sourceRows.length === 0) {
      return null;
    }

    const rows: RepaymentPlanReportRow[] = sourceRows
      .filter(
        (
          item,
        ): item is Record<string, unknown> =>
          !!item && typeof item === 'object',
      )
      .map((item) => {
        const month = String(
          item['month'] ?? '',
        ).slice(0, 10);

        const rawQuarter = String(
          item['quarter'] ?? '',
        ).trim();

        return {
          prpsdDsbmntSeq: this.toNumber(
            item['prpsdDsbmntSeq'],
          ),
          // Not returned by the backend for every proposal — derive the
          // calendar-quarter label from the tranche month instead of
          // leaving it blank, unless the backend did supply one.
          quarter:
            rawQuarter ||
            this.getQuarterLabel(month),
          type: this.getRepaymentTermLabel(
            item['rpymntTerm'],
          ),
          month,
          trancheAmount: this.toNumber(
            item['prpsdDsbmntAmount'],
          ),
          schedule: this.normalizeAmountRecord(
            item['installmentsByPeriod'],
          ),
        };
      })
      .sort((first, second) =>
        first.month.localeCompare(second.month),
      );

    const columns = this.derivePeriodColumns(
      rows.map((row) => row.schedule),
    );

    const totalDisbursementAmount = this.roundAmount(
      rows.reduce(
        (total, row) => total + row.trancheAmount,
        0,
      ),
    );

    const grandTotal = this.roundAmount(
      rows.reduce(
        (total, row) =>
          total + this.getRepaymentRowTotal(row),
        0,
      ),
    );

    return {
      columns,
      rows,
      totalDisbursementAmount,
      grandTotal,
      openingExposure: this.openingExposure,
    };
  }

  formatRepaymentPeriod(period: string): string {
    const parts = String(period)
      .slice(0, 10)
      .split('-');

    if (parts.length < 2) {
      return period;
    }

    const year = Number(parts[0]);
    const month = Number(parts[1]);

    if (
      !Number.isFinite(year) ||
      !Number.isFinite(month)
    ) {
      return period;
    }

    return new Intl.DateTimeFormat('en-GB', {
      month: 'short',
      year: '2-digit',
    }).format(new Date(year, month - 1, 1));
  }

  /** e.g. '2025-01-15' -> 'Jan-Mar 2025' (the calendar quarter containing that month). */
  private getQuarterLabel(month: string): string {
    const parts = String(month ?? '')
      .slice(0, 10)
      .split('-');

    if (parts.length < 2) {
      return '';
    }

    const year = Number(parts[0]);
    const monthNum = Number(parts[1]);

    if (
      !Number.isFinite(year) ||
      !Number.isFinite(monthNum)
    ) {
      return '';
    }

    const quarterStartMonth =
      Math.floor((monthNum - 1) / 3) * 3 + 1;

    const startLabel = new Intl.DateTimeFormat(
      'en-US',
      { month: 'short' },
    ).format(
      new Date(year, quarterStartMonth - 1, 1),
    );

    const endLabel = new Intl.DateTimeFormat(
      'en-US',
      { month: 'short' },
    ).format(
      new Date(year, quarterStartMonth + 1, 1),
    );

    return `${startLabel}-${endLabel} ${year}`;
  }

  /** Repayment Plan values are reported in full PKR, as entered on the Repayment screen. */
  formatAmount(value: number): string {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(value) || 0);
  }

  /** Non-deleted term type codes (`typ`) from the Get Credit Term Parameters response. */
  private extractRepaymentTermCodes(response: unknown): unknown[] {
    const source = response as { crdtTrmParamsLst?: unknown; data?: { crdtTrmParamsLst?: unknown } } | null;
    const list = source?.crdtTrmParamsLst ?? source?.data?.crdtTrmParamsLst;

    if (!Array.isArray(list)) {
      return [];
    }

    return list
      .filter((row) => !!row && Number(row.delFlg ?? 0) !== 1)
      .map((row) => row.typ ?? row.rpymntTerm);
  }

  /** Cover page facility list: unique labels of the repayment terms the user added. */
  private getCoverFacilities(): string[] {
    const labels = this.repaymentTermCodes
      .map((code) => this.getRepaymentTermLabel(code))
      .filter((label) => !!label);

    return [...new Set(labels)];
  }

  /** Label of a repayment term (ref code group 13, PKL_GRP.FACILITY_TYPE) code; '' when unknown. */
  private getRepaymentTermLabel(code: unknown): string {
    return this.pickListLabel(PKL_GRP.FACILITY_TYPE, code);
  }

  /** Repayment Plan data of the report's Repayment Plan section, if present. */
  private getCurrentRepaymentPlanData(): RepaymentPlanReportData | null {
    const section = this.reportSections.find((candidate) =>
      this.isRepaymentPlanSection(candidate),
    );

    return section ? this.getRepaymentPlanData(section) : null;
  }

  hasRepaymentInstallment(
    row: RepaymentPlanReportRow,
    period: string,
  ): boolean {
    return Object.prototype.hasOwnProperty.call(
      row.schedule,
      period,
    );
  }

  getRepaymentRowTotal(
    row: RepaymentPlanReportRow,
  ): number {
    return this.roundAmount(
      Object.values(row.schedule).reduce(
        (total, value) =>
          total + this.toNumber(value),
        0,
      ),
    );
  }

  getRepaymentColumnTotal(
    data: RepaymentPlanReportData,
    period: string,
  ): number {
    return this.roundAmount(
      data.rows.reduce(
        (total, row) =>
          total + this.toNumber(row.schedule[period]),
        0,
      ),
    );
  }

  /**
   * Repayment Plan split into one table per repayment term type (e.g.
   * Conventional / Islamic). Cached per data object so change detection
   * does not rebuild the groups on every pass.
   */
  getRepaymentPlanTypeGroups(
    data: RepaymentPlanReportData,
  ): RepaymentPlanTypeGroup[] {
    let groups = this.repaymentPlanTypeGroupsCache.get(data);

    if (!groups) {
      groups = splitRepaymentPlanByType(data);
      this.repaymentPlanTypeGroupsCache.set(data, groups);
    }

    return groups;
  }

  private readonly repaymentPlanTypeGroupsCache = new WeakMap<
    RepaymentPlanReportData,
    RepaymentPlanTypeGroup[]
  >();

  trackByRepaymentColumn(
    index: number,
    column: string,
  ): string {
    return column;
  }

  trackByRepaymentRow(
    index: number,
    row: RepaymentPlanReportRow,
  ): number {
    return row.prpsdDsbmntSeq || index;
  }

  /* ---------- Disbursement and Repayment Summary (2646) ---------- */

  /**
   * One table per repayment term type, built from the live saved plan
   * (GET /rpymnt-plan/get-prpsd-dsbmnt-rpymnt). Empty when the API returned
   * nothing, in which case the section block is shown as a single table.
   */
  private disbursementSummaryGroups: DisbursementSummaryTypeGroup[] = [];

  getDisbursementSummaryGroups(
    section: ReportSection,
  ): DisbursementSummaryTypeGroup[] | null {
    if (this.disbursementSummaryGroups.length > 0) {
      return this.disbursementSummaryGroups;
    }

    const data = this.getDisbursementRepaymentSummaryData(section);

    return data ? [{ type: '', data }] : null;
  }

  private buildDisbursementSummaryGroups(
    response: unknown,
  ): DisbursementSummaryTypeGroup[] {
    const items = this.asArray(response).filter(
      (item): item is Record<string, unknown> =>
        !!item &&
        typeof item === 'object' &&
        Number(item['delFlg'] ?? 0) !== 1,
    );

    const groups: { term: number | null; rows: DisbursementRepaymentSummaryReportRow[] }[] = [];

    for (const item of items) {
      const term =
        item['rpymntTerm'] != null ? Number(item['rpymntTerm']) : null;

      let group = groups.find((candidate) => candidate.term === term);

      if (!group) {
        group = { term, rows: [] };
        groups.push(group);
      }

      group.rows.push({
        month: String(item['schMth'] ?? '').slice(0, 10),
        proposedDisbursement: this.toNumber(item['prpsdDsbmnt']),
        proposedRepayment: this.toNumber(item['prpsdRpymnt']),
        scheduledDisbursement: this.toNumber(item['schDsbmnt']),
        scheduledRepayment: this.toNumber(item['schRpymnt']),
        maximumExposure: this.toNumber(item['maxExpsr']),
      });
    }

    return groups.map((group) => {
      const rows = group.rows.sort((first, second) =>
        first.month.localeCompare(second.month),
      );
      const sum = (key: keyof Omit<DisbursementRepaymentSummaryReportRow, 'month' | 'maximumExposure'>) =>
        this.roundAmount(rows.reduce((total, row) => total + row[key], 0));

      return {
        type: this.summaryTermLabel(group.term),
        data: {
          rows,
          openingExposure: this.openingExposure,
          totals: {
            proposedDisbursement: sum('proposedDisbursement'),
            proposedRepayment: sum('proposedRepayment'),
            scheduledDisbursement: sum('scheduledDisbursement'),
            scheduledRepayment: sum('scheduledRepayment'),
          },
        },
      };
    });
  }

  /** The plan stores the term's `refCd` (17 Conventional, 18 Islamic) from ref code group 13. */
  private summaryTermLabel(term: number | null): string {
    if (term == null) {
      return '';
    }

    const option = this.pickListService
      .getOptionsSync(PKL_GRP.FACILITY_TYPE)
      .find((candidate) => Number(candidate.original?.refCd) === term);

    return String(option?.label ?? '').trim() || this.getRepaymentTermLabel(term);
  }

  getDisbursementRepaymentSummaryData(
    section: ReportSection,
  ): DisbursementRepaymentSummaryReportData | null {
    // Each block's jsonData IS the summary-row list directly — there is no
    // `rows`/`summaryRows` wrapper object to search for.
    const latestBlock = this.getLatestBlock(section);

    if (
      !latestBlock ||
      !Array.isArray(latestBlock.jsonData)
    ) {
      return null;
    }

    const rows: DisbursementRepaymentSummaryReportRow[] = (
      latestBlock.jsonData as unknown[]
    )
      .filter(
        (
          item,
        ): item is Record<string, unknown> =>
          !!item && typeof item === 'object',
      )
      .map((item) => ({
        month: String(
          item['schMth'] ?? '',
        ).slice(0, 10),
        proposedDisbursement: this.toNumber(
          item['prpsdDsbmtAmt'],
        ),
        proposedRepayment: this.toNumber(
          item['prpsdRpmtAmt'],
        ),
        scheduledDisbursement: this.toNumber(
          item['schDsbmtAmt'],
        ),
        scheduledRepayment: this.toNumber(
          item['schRpmtAmt'],
        ),
        maximumExposure: this.toNumber(
          item['maxExpsrAmt'],
        ),
      }))
      .sort((first, second) =>
        first.month.localeCompare(second.month),
      );

    const totals = {
      proposedDisbursement: this.roundAmount(
        rows.reduce(
          (total, row) =>
            total + row.proposedDisbursement,
          0,
        ),
      ),
      proposedRepayment: this.roundAmount(
        rows.reduce(
          (total, row) =>
            total + row.proposedRepayment,
          0,
        ),
      ),
      scheduledDisbursement: this.roundAmount(
        rows.reduce(
          (total, row) =>
            total + row.scheduledDisbursement,
          0,
        ),
      ),
      scheduledRepayment: this.roundAmount(
        rows.reduce(
          (total, row) =>
            total + row.scheduledRepayment,
          0,
        ),
      ),
    };

    return {
      rows,
      openingExposure: this.openingExposure,
      totals,
    };
  }

  formatSummaryMonth(month: string): string {
    const match =
      /^(\d{4})-(\d{2})-\d{2}$/.exec(
        String(month ?? '').slice(0, 10),
      );

    if (!match) {
      return month || '-';
    }

    const year = Number(match[1]);
    const monthIndex = Number(match[2]) - 1;

    if (
      !Number.isFinite(year) ||
      !Number.isFinite(monthIndex)
    ) {
      return month;
    }

    return new Intl.DateTimeFormat('en-GB', {
      month: 'short',
      year: '2-digit',
    }).format(new Date(year, monthIndex, 1));
  }

  trackBySummaryRow(
    index: number,
    row: DisbursementRepaymentSummaryReportRow,
  ): string {
    return row.month || String(index);
  }

  /* ---------- shared refValSeq-section helpers ---------- */

  /**
   * Finds the first jsonData entry (across every block in this section, in
   * order) whose raw shape satisfies `matches`. jsonData is normalized to
   * `SectionJsonData[]` for the generic block renderer, but these three
   * report-only sections carry a different, section-specific shape — so we
   * read the underlying object as `Record<string, unknown>` instead of
   * forcing it through the `SectionJsonData` interface.
   */
  private findRawJsonItem(
    block: CreditSectionBlock,
    matches: (
      candidate: Record<string, unknown>,
    ) => boolean,
  ): Record<string, unknown> | null {
    if (!Array.isArray(block.jsonData)) {
      return null;
    }

    for (const item of block.jsonData as unknown[]) {
      if (!item || typeof item !== 'object') {
        continue;
      }

      const candidate =
        item as Record<string, unknown>;

      if (matches(candidate)) {
        return candidate;
      }
    }

    return null;
  }

  private normalizeForComparison(
    value: unknown,
  ): string {
    return String(value ?? '')
      .trim()
      .toLowerCase();
  }

  /**
   * These report-only sections can accumulate more than one saved block for
   * the same refValSeq (each save appears to create a new block rather than
   * updating one) — rendering all of them would duplicate/mix stale data.
   * Picks the single most recent block: highest `crdtPrpslBlkSeq`, falling
   * back to the latest `lastUpdDt` on a tie. `section.blocks` already
   * excludes `delFlg === 1` entries (filtered out when building
   * `allSectionData`), so this only has to pick "latest", not "non-deleted".
   */
  private getLatestBlock(
    section: ReportSection,
  ): CreditSectionBlock | null {
    if (section.blocks.length === 0) {
      return null;
    }

    return [...section.blocks].sort((first, second) => {
      const blockSeqDiff =
        Number(second.crdtPrpslBlkSeq ?? 0) -
        Number(first.crdtPrpslBlkSeq ?? 0);

      if (blockSeqDiff !== 0) {
        return blockSeqDiff;
      }

      return (
        this.toTimestamp(second.lastUpdDt) -
        this.toTimestamp(first.lastUpdDt)
      );
    })[0];
  }

  private toTimestamp(
    value: string | null | undefined,
  ): number {
    if (!value) {
      return 0;
    }

    const time = new Date(value).getTime();

    return Number.isFinite(time) ? time : 0;
  }

  private toNumber(
    value: unknown,
    fallback = 0,
  ): number {
    const numeric = Number(value ?? fallback);
    return Number.isFinite(numeric)
      ? numeric
      : fallback;
  }

  private toNumberOrNull(
    value: unknown,
  ): number | null {
    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return null;
    }

    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }

  private roundAmount(value: number): number {
    return (
      Math.round(
        (Number(value) + Number.EPSILON) * 100,
      ) / 100
    );
  }

  private normalizeAmountRecord(
    value: unknown,
  ): Record<string, number> {
    if (
      !value ||
      typeof value !== 'object' ||
      Array.isArray(value)
    ) {
      return {};
    }

    const normalized: Record<string, number> = {};

    Object.entries(
      value as Record<string, unknown>,
    ).forEach(([key, raw]) => {
      normalized[key] = this.toNumber(raw);
    });

    return normalized;
  }

  private normalizeNullableAmountRecord(
    value: unknown,
  ): Record<string, number | null> {
    if (
      !value ||
      typeof value !== 'object' ||
      Array.isArray(value)
    ) {
      return {};
    }

    const normalized: Record<
      string,
      number | null
    > = {};

    Object.entries(
      value as Record<string, unknown>,
    ).forEach(([key, raw]) => {
      normalized[key] = this.toNumberOrNull(raw);
    });

    return normalized;
  }

  private derivePeriodColumns(
    records: Array<
      Record<string, number | null> | Record<string, number>
    >,
  ): string[] {
    const periods = new Set<string>();

    records.forEach((record) => {
      Object.keys(record ?? {}).forEach(
        (period) => periods.add(period),
      );
    });

    return Array.from(periods).sort(
      (first, second) =>
        first.localeCompare(second, undefined, {
          numeric: true,
          sensitivity: 'base',
        }),
    );
  }

  /* ====================================================== */
  /* NESTED REPORT SECTIONS */
  /* 2527, 2530, 2531 and other rich sections */
  /* ====================================================== */

  hasNestedReportBlocks(
    section: ReportSection,
  ): boolean {
    return (
      this.getNestedReportBlocks(
        section,
      ).length > 0
    );
  }

  getNestedReportBlocks(
    section: ReportSection,
  ): NestedReportBlock[] {
    return section.blocks
      .flatMap(
        (
          sectionBlock:
            CreditSectionBlock,
        ) =>
          Array.isArray(
            sectionBlock.jsonData,
          )
            ? sectionBlock.jsonData
            : [],
      )
      .flatMap(
        (
          jsonItem:
            SectionJsonData,
        ) =>
          Array.isArray(
            jsonItem.blocks,
          )
            ? jsonItem.blocks
            : [],
      )
      .filter(
        (
          contentBlock:
            NestedReportBlock,
        ) =>
          Number(
            contentBlock?.delFlg ?? 0,
          ) !== 1,
      );
  }

  getNestedBlockLines(
    content:
      string | undefined,
  ): string[] {
    return String(content ?? '')
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  showNestedBlockTitle(
    section: ReportSection,
    block: NestedReportBlock,
  ): boolean {
    const sectionTitle = this.normalizeTitleForComparison(
      section.title,
    );

    const blockTitle = this.normalizeTitleForComparison(
      block.title,
    );

    return Boolean(
      blockTitle && blockTitle !== sectionTitle,
    );
  }

  private normalizeTitleForComparison(
    value: unknown,
  ): string {
    return String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  hasTableSubColumns(
    table: NestedTable,
  ): boolean {
    return (
      Array.isArray(table?.columns) &&
      table.columns.some(
        (
          column:
            NestedTableColumn,
        ) =>
          this.getSubColumns(
            column,
          ).length > 0,
      )
    );
  }

  getSubColumns(
    column:
      NestedTableColumn,
  ): string[] {
    return Array.isArray(
      column?.subColumns,
    )
      ? column.subColumns
      : [];
  }

  getColumnColspan(
    column:
      NestedTableColumn,
  ): number | null {
    const subColumnCount =
      this.getSubColumns(
        column,
      ).length;

    return subColumnCount > 0
      ? subColumnCount
      : null;
  }

  getColumnRowspan(
    table: NestedTable,
    column:
      NestedTableColumn,
  ): number | null {
    const hasOwnSubColumns =
      this.getSubColumns(
        column,
      ).length > 0;

    return (
      this.hasTableSubColumns(table) &&
      !hasOwnSubColumns
    )
      ? 2
      : null;
  }

  trackByNestedBlock(
    index: number,
    block: NestedReportBlock,
  ): string {
    return [
      block.type,
      block.title ?? '',
      index,
    ].join('-');
  }

  getPhysicalColumnCount(
    table: NestedTable,
  ): number {
    if (!table || !Array.isArray(table.columns)) {
      return 0;
    }

    return table.columns.reduce((total, column) => {
      const subColumnCount = this.getSubColumns(
        column,
      ).length;

      return total + (subColumnCount > 0 ? subColumnCount : 1);
    }, 0);
  }

  /* ====================================================== */
  /* REGULAR BLOCKS */
  /* ====================================================== */

  /** True for rich-text HTML (RichTextEditor output) vs legacy plain text. */
  isRichText(value: unknown): boolean {
    return isRichTextHtml(value);
  }

  isBlockType(
    block: CreditSectionBlock,
    type: number,
  ): boolean {
    return (
      Number(block?.blkTyp) ===
      Number(type)
    );
  }

  hasBlockContent(
    block: CreditSectionBlock,
  ): boolean {
    return Boolean(
      this.getContent(block).trim(),
    );
  }

  /* ---------- Highlights (blkTyp = 3) ---------- */

  getHighlightItemContent(
    item: SectionJsonData,
  ): string {
    const content = String(item?.content ?? '').trim();

    if (content) {
      return content;
    }

    if (Array.isArray(item?.paragraphs)) {
      return joinRichText(item.paragraphs);
    }

    return '';
  }

  showHighlightItemTitle(
    section: ReportSection,
    item: SectionJsonData,
  ): boolean {
    const sectionTitle = this.normalizeTitleForComparison(
      section.title,
    );

    const itemTitle = this.normalizeTitleForComparison(
      item?.title,
    );

    return Boolean(
      itemTitle && itemTitle !== sectionTitle,
    );
  }

  getBullets(
    block: CreditSectionBlock,
  ): string[] {
    if (
      !Array.isArray(
        block?.jsonData,
      )
    ) {
      return [];
    }

    const bullets: string[] = [];

    block.jsonData.forEach(
      (data: SectionJsonData) => {
        if (
          Array.isArray(
            data?.bullets,
          )
        ) {
          data.bullets.forEach(
            (item: string) => {
              const value = String(
                item ?? '',
              ).trim();

              if (value) {
                bullets.push(value);
              }
            },
          );
        }

        if (
          data?.showBullet &&
          data?.content
        ) {
          String(data.content)
            .split('\n')
            .map((item) =>
              item.trim(),
            )
            .filter(Boolean)
            .forEach((item) =>
              bullets.push(item),
            );
        }
      },
    );

    return bullets;
  }

  hasBullets(
    block: CreditSectionBlock,
  ): boolean {
    return (
      this.getBullets(block)
        .length > 0
    );
  }

  getContent(
    block: CreditSectionBlock,
  ): string {
    if (!block) {
      return '';
    }

    const jsonItems =
      Array.isArray(
        block.jsonData,
      )
        ? block.jsonData
        : [];

    const jsonContent =
      jsonItems
        .filter(
          (item) =>
            !item?.showBullet &&
            !Array.isArray(
              item?.blocks,
            ),
        )
        .map((item) =>
          this.getHighlightItemContent(item),
        )
        .filter(Boolean)
        .join('\n');

    if (jsonContent) {
      return jsonContent;
    }

    return String(
      block.txtData ?? '',
    ).trim();
  }

  /* ====================================================== */
  /* RECOMMENDATION */
  /* ====================================================== */

  isRecommendationSection(
    section: ReportSection,
  ): boolean {
    return (
      this.getRecommendationRows(
        section,
      ).length > 0
    );
  }

  getRecommendationRows(
    section: ReportSection,
  ): SectionJsonData[] {
    return section.blocks
      .flatMap(
        (
          block:
            CreditSectionBlock,
        ) =>
          Array.isArray(
            block.jsonData,
          )
            ? block.jsonData
            : [],
      )
      .filter(
        (
          item:
            SectionJsonData,
        ) =>
          Array.isArray(
            item.recommendationBlocks,
          ),
      );
  }

  getRecommendationBlocks(
    item: SectionJsonData,
  ) {
    return Array.isArray(
      item?.recommendationBlocks,
    )
      ? item.recommendationBlocks
      : [];
  }

  /** Row-scoped number for a numbered recommendation item: 1.1, 1.2… under row "1". */
  getRecommendationNumber(
    row: SectionJsonData,
    recommendation: { type?: string; items?: string[] },
    itemIndex: number,
  ): string {
    return scopedListLabel(
      row?.serialNo,
      this.getRecommendationBlocks(row),
      recommendation,
      itemIndex,
      (block) => block?.type === 'numbered-list',
      (block) => this.getRecommendationItems(block).length,
    );
  }

  getRecommendationItems(
    recommendation:
      { items?: string[] },
  ): string[] {
    return Array.isArray(
      recommendation?.items,
    )
      ? recommendation.items
          .map((item) =>
            String(
              item ?? '',
            ).trim(),
          )
          .filter(Boolean)
      : [];
  }

  removeExistingNumber(
    value: string,
  ): string {
    return String(value ?? '')
      .replace(
        /^\s*\d+[.)]\s*/,
        '',
      )
      .trim();
  }

  /* ====================================================== */
  /* INSTITUTIONAL RATING */
  /* ====================================================== */

  isInstitutionalRatingSection(
    section: ReportSection,
  ): boolean {
    return (
      this.getInstitutionalRatingData(
        section,
      ) !== null
    );
  }

  getInstitutionalRatingData(
    section: ReportSection,
  ): SectionJsonData | null {
    const isKnownRatingSection =
      section.type === SectionType.IRS_RATING ||
      section.type === SectionType.IRS_RATING_SUMRY ||
      Number(section.refValSeq) ===
        Number(PKL_GRP.INSTITUTIONAL_RATING);

    for (const block of section.blocks) {
      if (!Array.isArray(block.jsonData)) {
        continue;
      }

      const ratingData = block.jsonData.find(
        (item: SectionJsonData) =>
          this.isInstitutionalRatingData(
            item,
            isKnownRatingSection,
          ),
      );

      if (ratingData) {
        return ratingData;
      }
    }

    return null;
  }

  private isInstitutionalRatingData(
    item: SectionJsonData,
    isKnownRatingSection: boolean,
  ): boolean {
    const rows = Array.isArray(item?.rows)
      ? item.rows
      : [];

    const hasRatingRows = rows.some(
      (row) =>
        row &&
        Boolean(
          row.category ||
            row.weight ||
            row.currentScore ||
            row.currentRating ||
            row.previousScore ||
            row.previousRating,
        ),
    );

    const finalScore = item?.finalScore;

    const hasRatingFinalScore = Boolean(
      finalScore &&
        (finalScore.currentScore ||
          finalScore.currentRating ||
          finalScore.previousScore ||
          finalScore.previousRating),
    );

    if (hasRatingRows || hasRatingFinalScore) {
      return true;
    }

    // A generic object only carrying `introduction`/`conclusion`/`rows`
    // (with no rating-specific fields) is NOT institutional rating data
    // unless the section itself is a known institutional-rating section.
    return (
      isKnownRatingSection &&
      (Boolean(item?.introduction) ||
        Boolean(item?.conclusion) ||
        rows.length > 0)
    );
  }

  getInstitutionalRatingRows(
    data: SectionJsonData,
  ) {
    return Array.isArray(data?.rows)
      ? data.rows
      : [];
  }

  /* ====================================================== */
  /* TRACK BY */
  /* ====================================================== */

  trackBySection(
    index: number,
    section: ReportSection,
  ): number {
    return section.refValSeq;
  }

  trackByBlock(
    index: number,
    block: CreditSectionBlock,
  ): number {
    return (
      block.crdtPrpslBlkSeq ||
      index
    );
  }

  /* ====================================================== */
  /* ROUTE */
  /* ====================================================== */

  private getCreditProposalSequence(): number {
    let currentRoute:
      ActivatedRoute | null =
      this.route;

    while (currentRoute) {
      const routeValue =
        currentRoute.snapshot
          .paramMap.get('id') ??
        currentRoute.snapshot
          .paramMap.get('oid');

      const sequence =
        Number(routeValue);

      if (
        routeValue &&
        Number.isFinite(sequence) &&
        sequence > 0
      ) {
        return sequence;
      }

      currentRoute =
        currentRoute.parent;
    }

    return 0;
  }
}